import type { FilterState } from "./filters/types";

const SORT_KEY = "sort";
const START_KEY = "start";
const QUERY_KEY = "q";
const FILTER_PREFIX = "f_";
const SLIDER_RE = /^\d+\.?\d*,\d+\.?\d*$/;

export interface ListUrlState {
  filters: FilterState;
  sort: string;
  start: number;
}

export function getSearchQuery(): string {
  return new URLSearchParams(window.location.search).get(QUERY_KEY) ?? "";
}

export function searchUrl(query: string): string {
  return `${window.DUSK?.searchUrl ?? "/search"}?${new URLSearchParams({ [QUERY_KEY]: query })}`;
}

export function parseListUrlState(): ListUrlState {
  const params = new URLSearchParams(window.location.search);
  const sort = params.get(SORT_KEY) ?? "";
  const start = parseInt(params.get(START_KEY) ?? "0", 10) || 0;

  const keyMap = new Map<string, string[]>();
  for (const [key, value] of params.entries()) {
    if (!key.startsWith(FILTER_PREFIX)) continue;
    const filterKey = key.slice(FILTER_PREFIX.length);
    const group = keyMap.get(filterKey);
    if (group) group.push(value);
    else keyMap.set(filterKey, [value]);
  }

  const filters: FilterState = {};
  for (const [key, values] of keyMap) {
    if (values.length === 1 && SLIDER_RE.test(values[0])) {
      const [min, max] = values[0].split(",").map(Number);
      filters[key] = [min, max];
    } else {
      filters[key] = values;
    }
  }

  return { filters, sort, start };
}

export function buildListSearch(filters: FilterState, sort: string, start: number): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value.length === 0) continue;
    if (typeof value[0] === "number") {
      params.set(FILTER_PREFIX + key, (value as [number, number]).join(","));
    } else {
      for (const v of value as string[]) params.append(FILTER_PREFIX + key, v);
    }
  }
  if (sort) params.set(SORT_KEY, sort);
  if (start > 0) params.set(START_KEY, String(start));
  return params.toString();
}

export function pushListState(filters: FilterState, sort: string): void {
  const search = buildListSearch(filters, sort, 0);
  history.pushState(null, "", `${location.pathname}${search ? `?${search}` : ""}`);
}

export function replaceListState(filters: FilterState, sort: string, start: number): void {
  const search = buildListSearch(filters, sort, start);
  history.replaceState(null, "", `${location.pathname}${search ? `?${search}` : ""}`);
}

export function pushSearchState(filters: FilterState, sort: string, searchQuery: string): void {
  const params = new URLSearchParams(buildListSearch(filters, sort, 0));
  if (searchQuery) params.set(QUERY_KEY, searchQuery);
  const qs = params.toString();
  history.pushState(null, "", `${location.pathname}${qs ? `?${qs}` : ""}`);
}

export function replaceSearchState(
  filters: FilterState,
  sort: string,
  start: number,
  searchQuery: string,
): void {
  const params = new URLSearchParams(buildListSearch(filters, sort, start));
  if (searchQuery) params.set(QUERY_KEY, searchQuery);
  const qs = params.toString();
  history.replaceState(null, "", `${location.pathname}${qs ? `?${qs}` : ""}`);
}

export function filtersEqual(a: FilterState, b: FilterState): boolean {
  const keysA = Object.keys(a).sort();
  const keysB = Object.keys(b).sort();
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    const va = a[key];
    const vb = b[key];
    if (!vb) return false;
    if (va.length !== vb.length) return false;
    for (let i = 0; i < va.length; i++) {
      if (va[i] !== vb[i]) return false;
    }
  }
  return true;
}
